import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { buildPublicApiUrl } from '../lib/api';

const formatPrice = (value) => {
  if (value === undefined || value === null || value === '') return '';
  const number = Number(value);
  if (Number.isNaN(number)) return value;
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(number);
  } catch (error) {
    return number.toFixed(2);
  }
};

const extractProducts = (section) => {
  if (!section) return [];
  if (Array.isArray(section.products)) return section.products;
  if (Array.isArray(section.menuSectionProducts)) {
    return section.menuSectionProducts.map((item) => item.product ?? item.shopProduct ?? item);
  }
  if (Array.isArray(section.items)) return section.items;
  return [];
};

const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const unwrap = (value) => {
  let current = value;
  const visited = new Set();
  while (isRecord(current) && !visited.has(current)) {
    visited.add(current);
    if (current.data && current.data !== current) {
      current = current.data;
      continue;
    }
    if (current.result && current.result !== current) {
      current = current.result;
      continue;
    }
    break;
  }
  return current;
};

const findFirstRecord = (candidates) => {
  for (const candidate of candidates) {
    const unwrapped = unwrap(candidate);
    if (isRecord(unwrapped)) {
      return unwrapped;
    }
  }
  return null;
};

const parseMenusResponse = (payload) => {
  if (payload == null) return { shop: null, menus: [] };

  const primary = unwrap(payload);

  if (Array.isArray(primary)) {
    return { shop: null, menus: primary };
  }

  if (!isRecord(primary)) {
    return { shop: null, menus: [] };
  }

  const shop = findFirstRecord([
    primary.shop,
    primary.details,
    primary.shopDetails,
    primary.store,
    primary.storeDetails,
  ]) || (primary.name || primary.title ? primary : null);

  const menusCandidate = unwrap(
    primary.menus ??
      primary.menuSections ??
      primary.sections ??
      primary.items ??
      primary.records ??
      primary.list,
  );

  const menus = Array.isArray(menusCandidate)
    ? menusCandidate
    : Array.isArray(primary.items)
    ? primary.items
    : [];

  return {
    shop: isRecord(shop) ? shop : null,
    menus,
  };
};

const ShopMenusPage = () => {
  const { shopSlug } = useParams();
  const [shop, setShop] = useState(null);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    const loadMenus = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!shopSlug) {
          setError('Shop not specified.');
          setShop(null);
          setMenus([]);
          return;
        }

        const endpoint = buildPublicApiUrl(`shops/${encodeURIComponent(shopSlug)}/menus`);

        const response = await fetch(endpoint, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
          throw new Error('Unable to load menus for this shop right now.');
        }

        const payload = await response.json();
        const { shop: shopDetails, menus: fetchedMenus } = parseMenusResponse(payload);

        setShop(shopDetails);
        setMenus(Array.isArray(fetchedMenus) ? fetchedMenus : []);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error(err);
          setError(err.message || 'Something went wrong while loading menus.');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadMenus();

    return () => controller.abort();
  }, [shopSlug]);

  useEffect(() => {
    if (shop?.name) {
      document.title = `${shop.name} | Menus`;
    } else {
      document.title = 'Shop Menus';
    }
  }, [shop?.name]);

  const heading = useMemo(() => {
    if (shop?.name) return shop.name;
    if (shopSlug) {
      return shopSlug
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }
    return 'Shop Menus';
  }, [shop?.name, shopSlug]);

  return (
    <div className="bg-white py-16">
      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8">
        <header className="border-b border-slate-200 pb-6">
          <p className="text-sm font-semibold uppercase tracking-wide text-indigo-600">Menus</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {heading}
          </h1>
          {shop?.description && (
            <p className="mt-3 max-w-3xl text-base text-slate-600">{shop.description}</p>
          )}
        </header>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-indigo-500 border-t-transparent" />
            <span className="ml-4 text-lg text-slate-600">Loading menus...</span>
          </div>
        )}

        {!loading && error && (
          <div className="mt-10 rounded-lg border border-rose-200 bg-rose-50 p-6 text-rose-700">
            <h2 className="text-lg font-semibold">We couldn&apos;t load the menus</h2>
            <p className="mt-2 text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="mt-10 space-y-10">
            {menus.length === 0 ? (
              <p className="text-base text-slate-600">
                No menus are available for this shop right now. Please check back later.
              </p>
            ) : (
              menus.map((menu) => {
                const products = extractProducts(menu);
                return (
                  <section
                    key={menu.id ?? menu.slug ?? menu.name}
                    className="rounded-2xl border border-slate-200 bg-slate-50/60 shadow-sm"
                  >
                    <div className="border-b border-slate-200 bg-white/90 p-6 sm:p-8">
                      <h2 className="text-2xl font-semibold text-slate-900">{menu.name ?? menu.title}</h2>
                      {menu.description && (
                        <p className="mt-2 text-sm text-slate-600">{menu.description}</p>
                      )}
                    </div>

                    {products.length === 0 ? (
                      <p className="px-6 py-8 text-sm text-slate-600">
                        No products available in this menu yet.
                      </p>
                    ) : (
                      <ul className="divide-y divide-slate-200">
                        {products.map((rawProduct) => {
                          const product = rawProduct?.product ?? rawProduct;
                          const price =
                            product?.price ??
                            product?.sellingPrice ??
                            product?.mrp ??
                            product?.basePrice;

                          return (
                            <li
                              key={product?.id ?? product?.sku ?? product?.name}
                              className="flex flex-col gap-4 px-6 py-5 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <div>
                                <h3 className="text-lg font-medium text-slate-900">
                                  {product?.name ?? product?.title ?? 'Untitled product'}
                                </h3>
                                {product?.description && (
                                  <p className="mt-1 text-sm text-slate-600">{product.description}</p>
                                )}
                              </div>
                              {price !== undefined && price !== null && price !== '' && (
                                <div className="text-right text-base font-semibold text-slate-900">
                                  {formatPrice(price)}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </section>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopMenusPage;
